// Which items in a journal's manuscript are posts (`JournalPosts.posts(in:)`, SuiteModel/Journal.swift), kept apart from
// journal.ts so the edits that number posts can read it without an import cycle.
import type { TreeNode } from '../contract.ts';
import { tree, type ArrangementTreeNode } from './arrangement.ts';
import type { Arrangement } from './documents.ts';

/** `JournalPosts.Post`: a post's node and fragment, the folder it's in (null at the top level), its sections (every
 *  item under it with a fragment, in reading order) and every node under it (folders without a fragment too). */
export interface JournalPostRef {
  nodeId: string;
  fragmentId: string;
  parentId: string | null;
  sections: { nodeId: string; fragmentId: string }[];
  nodes: Set<string>;
}

interface PostShape<T> { id: string; fragmentId: string | null; isFolder: boolean; children: T[] }

function postsIn<T>(nodes: T[], shape: (n: T) => PostShape<T>, parentId: string | null = null): JournalPostRef[] {
  const sections = (n: T): { nodeId: string; fragmentId: string }[] => shape(n).children.flatMap((c) => {
    const s = shape(c);
    return [...(s.fragmentId ? [{ nodeId: s.id, fragmentId: s.fragmentId }] : []), ...sections(c)];
  });
  const under = (n: T): string[] => shape(n).children.flatMap((c) => [shape(c).id, ...under(c)]);
  const out: JournalPostRef[] = [];
  for (const n of nodes) {
    const s = shape(n);
    if (!s.isFolder && s.fragmentId) out.push({ nodeId: s.id, fragmentId: s.fragmentId, parentId, sections: sections(n), nodes: new Set(under(n)) });
    else out.push(...postsIn(s.children, shape, s.id));
  }
  return out;
}

/** `JournalPosts.posts(in:)`: a folder is always a grouping and never a post; a document is a post when no document is
 *  above it (folders above don't count); everything under a post, at any depth, is a section of it. An item without a
 *  fragment counts as a folder. */
export function journalPosts(a: Arrangement | ArrangementTreeNode[]): JournalPostRef[] {
  const nodes = Array.isArray(a) ? a : tree(a);
  return postsIn(nodes, (n) => ({ id: n.node.id, fragmentId: n.node.fragmentID ?? null, isFolder: n.node.isFolder, children: n.children }));
}

/** `journalPosts` over a project view's tree (`Project.tree`). */
export function journalPostsInTree(nodes: TreeNode[]): JournalPostRef[] {
  return postsIn(nodes, (n) => ({ id: n.id, fragmentId: n.fragmentId, isFolder: n.isFolder, children: n.children }));
}

/** `JournalPosts.post(containing:in:)`: the post that is this item (node or fragment id) or holds it; null for a folder of
 *  posts or anything outside them. */
export function postContaining(id: string, posts: JournalPostRef[]): JournalPostRef | null {
  return posts.find((p) => p.nodeId === id || p.fragmentId === id || p.nodes.has(id) || p.sections.some((s) => s.fragmentId === id)) ?? null;
}

/** `JournalPosts.demoted(from:to:)`: posts in `before` that `after` would make sections (still in it, now under another
 *  post), each with the post that would hold it. */
export function demotedPosts(before: Arrangement, after: Arrangement): { post: JournalPostRef; holder: JournalPostRef }[] {
  const now = journalPosts(after);
  const still = new Set([...after.nodes.values()].flatMap((n) => (n.fragmentID !== undefined ? [n.fragmentID] : [])));
  return journalPosts(before).flatMap((post) => {
    if (!still.has(post.fragmentId) || now.some((p) => p.fragmentId === post.fragmentId)) return [];
    const holder = postContaining(post.fragmentId, now);
    return holder ? [{ post, holder }] : [];
  });
}

/** `Arrangement.moving(_:to:)`: these nodes (and what's under them) moved into `parentId`, positions aside. */
export function movingNodes(a: Arrangement, nodeIds: string[], parentId: string | null): Arrangement {
  const moving = new Set(nodeIds);
  const nodes = new Map(a.nodes);
  const above = (id: string): string[] => {
    const out: string[] = [];
    const seen = new Set([id]);
    let p = a.nodes.get(id)?.parentID;
    while (p !== undefined && a.nodes.has(p) && !seen.has(p)) { seen.add(p); out.push(p); p = a.nodes.get(p)?.parentID; }
    return out;
  };
  for (const id of nodeIds) {
    const n = nodes.get(id);
    if (!n || above(id).some((x) => moving.has(x))) continue;
    const { parentID: _, ...rest } = n;
    nodes.set(id, parentId !== null ? { ...rest, parentID: parentId } : rest);
  }
  return { ...a, nodes };
}

/** `Arrangement.converting(_:toFolder:)`. */
export function convertingNodes(a: Arrangement, nodeIds: string[], toFolder: boolean): Arrangement {
  const nodes = new Map(a.nodes);
  for (const id of nodeIds) { const n = nodes.get(id); if (n) nodes.set(id, { ...n, isFolder: toFolder }); }
  return { ...a, nodes };
}

/** `JournalPosts.sectionPlacement(for:in:)`: after a chosen section, inside a chosen folder of sections, else last in the
 *  post; null when the choice isn't in a post. */
export function sectionPlacement(nodeId: string, a: Arrangement): { after: string } | { inside: string } | null {
  const post = postContaining(nodeId, journalPosts(a));
  if (!post) return null;
  if (nodeId === post.nodeId) return { inside: post.nodeId };
  return a.nodes.get(nodeId)?.isFolder ? { inside: nodeId } : { after: nodeId };
}

/** `JournalPosts.postOnlyFields`: what a post has and a section doesn't show. */
export const POST_ONLY_FIELDS: ReadonlySet<string> = new Set(['status', 'post-number', 'publish-date']);

